import type { NamedSet } from 'zustand/middleware';

import type { Settlement } from '../../queries/settlementQuery';
import type { WalletData } from '../../queries/walletQuery';
import type { SortBy } from '../../utils/dateUtils';
import { compareSettlementDates } from '../../utils/dateUtils';
import type { ZustandStore } from '../store.type';

export const sortWalletOptions = {
	newest: 'desc',
	oldest: 'asc',
	spendLimit: 'desc',
} as const;

export const sortWalletMapper = {
	desc: 'DESC',
	asc: 'ASC',
	newest: 'CREATION_DATE',
	oldest: 'CREATION_DATE',
	spendLimit: 'AMOUNT',
};

export type SortWalletKey = keyof typeof sortWalletOptions;
type OrderWalletKey = (typeof sortWalletOptions)[SortWalletKey];

export type SingleWalletSortOption = {
	[K in SortWalletKey]: Record<K, OrderWalletKey>;
}[SortWalletKey];

type TeamOptions = { id: string; label: string };
type MemberOptions = { id: string; label: string };

type WalletState = {
	wallet: {
		cards: WalletData[];
		lastPageHasLoaded: boolean;
		availableStatusText: string;
		approvalStatusText: string;
		authPaddingPercentage: number;
		sortValue: SingleWalletSortOption;
		teamFilter: string;
		memberFilter: string;
		areFiltersManuallySet: boolean; // we need to track if the user changes or not the filters manually
		cardStatusFilter: 'active' | 'inactive';
		teamOptions: TeamOptions[] | any;
		memberOptions: MemberOptions[];
		walletDetails: WalletData | any;
		settlements: Settlement[];
		pager: {
			currentPage: number;
			hasNextPage: boolean;
		};
		previousPath: string;
	};
};

type WalletActions = {
	resetWalletFilters: () => void;
	setWalletCards: (cards: WalletState['wallet']['cards']) => void;
	setLastPageHasLoaded: (view: WalletState['wallet']['lastPageHasLoaded']) => void;
	setWalletSortValue: (sortValue: WalletState['wallet']['sortValue']) => void;
	setTeamFilter: (teamFilter: WalletState['wallet']['teamFilter']) => void;
	setMemberFilter: (memberFilter: WalletState['wallet']['memberFilter']) => void;
	setCardStatusFilter: (cardStatusFilter: WalletState['wallet']['cardStatusFilter']) => void;
	setTeamOptions: (teamOptions: WalletState['wallet']['teamOptions']) => void;
	setMemberOptions: (memberOptions: WalletState['wallet']['memberOptions']) => void;
	setAreFiltersManuallySet: (areFiltersManuallySet: WalletState['wallet']['areFiltersManuallySet']) => void;
	setSettlementsData: (settlements: WalletState['wallet']['settlements'], sortBy?: SortBy) => void;
	setWalletDetails: (cards: WalletState['wallet']['walletDetails']) => void;
	setCurrentWalletPage: (currentPage: WalletState['wallet']['pager']['currentPage']) => void;
	setHasNextPage: (hasNextPage: WalletState['wallet']['pager']['hasNextPage']) => void;
	setWalletPreviousPath: (previousPath: WalletState['wallet']['previousPath']) => void;
};

export type WalletSlice = WalletState & WalletActions;

const walletInitialState: WalletState = {
	wallet: {
		cards: [],
		lastPageHasLoaded: true,
		availableStatusText: 'Available',
		approvalStatusText: 'Approved',
		authPaddingPercentage: 7,
		sortValue: {
			newest: 'desc',
		},
		cardStatusFilter: 'active',
		teamFilter: 'all',
		memberFilter: 'all',
		areFiltersManuallySet: false,
		teamOptions: [{ id: 'all', label: 'All Teams' }],
		memberOptions: [{ id: 'all', label: 'All Members' }],
		settlements: [],
		walletDetails: {},
		pager: {
			currentPage: 0,
			hasNextPage: false,
		},
		previousPath: '/wallet',
	},
};

export function walletSlice(set: NamedSet<ZustandStore>): WalletSlice {
	return {
		...walletInitialState,
		resetWalletFilters(): void {
			set(
				(state: WalletState) => {
					state.wallet.sortValue = walletInitialState.wallet.sortValue;
					state.wallet.cardStatusFilter = walletInitialState.wallet.cardStatusFilter;
					state.wallet.teamFilter = walletInitialState.wallet.teamFilter;
					state.wallet.memberFilter = walletInitialState.wallet.memberFilter;
					state.wallet.pager.currentPage = walletInitialState.wallet.pager.currentPage;
					return state;
				},
				false,
				'[resetWalletFilters]',
			);
		},
		setWalletCards(cards: WalletState['wallet']['cards']): void {
			set(
				(state: WalletState) => {
					state.wallet.cards = cards;
					return state;
				},
				false,
				'[setWalletCards]',
			);
		},
		setWalletSortValue(sortValue: WalletState['wallet']['sortValue']): void {
			set(
				(state: WalletState) => {
					state.wallet.sortValue = sortValue;
					return state;
				},
				false,
				'[setWalletSortValue]',
			);
		},
		setWalletDetails(walletDetails: WalletState['wallet']['walletDetails']): void {
			set(
				(state: WalletState) => {
					state.wallet.walletDetails = walletDetails;
					return state;
				},
				false,
				'[setWalletDetails]',
			);
		},
		setTeamFilter(teamFilter: WalletState['wallet']['teamFilter']): void {
			set(
				(state: WalletState) => {
					state.wallet.teamFilter = teamFilter;
					return state;
				},
				false,
				'[setTeamFilter]',
			);
		},
		setMemberFilter(memberFilter: WalletState['wallet']['memberFilter']): void {
			set(
				(state: WalletState) => {
					state.wallet.memberFilter = memberFilter;
					return state;
				},
				false,
				'[setMemberFilter]',
			);
		},
		setAreFiltersManuallySet(areFiltersManuallySet: WalletState['wallet']['areFiltersManuallySet']): void {
			set(
				(state: WalletState) => {
					state.wallet.areFiltersManuallySet = areFiltersManuallySet;
					return state;
				},
				false,
				'[setAreFiltersManuallySet]',
			);
		},
		setCardStatusFilter(cardStatusFilter: WalletState['wallet']['cardStatusFilter']): void {
			set(
				(state: WalletState) => {
					state.wallet.cardStatusFilter = cardStatusFilter;
					return state;
				},
				false,
				'[setCardStatusFilter]',
			);
		},
		setLastPageHasLoaded(lastPageHasLoaded: WalletState['wallet']['lastPageHasLoaded']): void {
			set(
				(state: WalletState) => {
					state.wallet.lastPageHasLoaded = lastPageHasLoaded;
					return state;
				},
				false,
				'[setLastPageHasLoaded]',
			);
		},
		setTeamOptions(teamOptions: WalletState['wallet']['teamOptions']): void {
			set(
				(state: WalletState) => {
					state.wallet.teamOptions = teamOptions;
					return state;
				},
				false,
				'[setTeamOptions]',
			);
		},
		setMemberOptions(memberOptions: WalletState['wallet']['memberOptions']): void {
			set(
				(state: WalletState) => {
					state.wallet.memberOptions = memberOptions;
					return state;
				},
				false,
				'[setMemberOptions]',
			);
		},
		setSettlementsData(settlements: WalletState['wallet']['settlements'], sortBy: SortBy = 'most-recent'): void {
			set(
				(state: WalletState) => {
					const sortedSettlements = settlements?.toSorted((a, b) => compareSettlementDates(a, b, sortBy));
					state.wallet.settlements = sortedSettlements;
					return state;
				},
				false,
				'[setSettlementsData]',
			);
		},
		setCurrentWalletPage(currentPage: WalletState['wallet']['pager']['currentPage']) {
			set(
				(state: WalletState) => {
					state.wallet.pager.currentPage = currentPage;
					return state;
				},
				false,
				'[setCurrentWalletPage]',
			);
		},
		setHasNextPage(hasNextPage: WalletState['wallet']['pager']['hasNextPage']) {
			set(
				(state: WalletState) => {
					state.wallet.pager.hasNextPage = hasNextPage;
					return state;
				},
				false,
				'[setHasNextPage]',
			);
		},
		setWalletPreviousPath(previousPath: WalletState['wallet']['previousPath']) {
			set(
				(state: WalletState) => {
					state.wallet.previousPath = previousPath;
					return state;
				},
				false,
				'[setWalletPreviousPath]',
			);
		},
	};
}
